import { Component, ViewChild, OnInit } from '@angular/core';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { NotificationService } from 'app/_services/notification.service';
import { DynamicFormComponent } from '../_dynamic_form/containers/dynamic_form/dynamic-form.component';
import { CustomBuilderService } from '../_services/custom-bulider.service';
import { CookieService } from 'ngx-cookie-service';
import { CategoryService } from '../_services/category.service';
import { KioskCategoryService } from '../_services/kiosk-category.service';
import { ObjectFilterPipe } from '../_pipes/object-filter.pipe';
import { TreeNode } from 'primeng/api';

@Component({
    selector: 'app-custom-builders',
    templateUrl: './custom-builders.component.html',
    styleUrls: ['./custom-builders.component.css']
})
export class CustomBuildersComponent implements OnInit {

    @ViewChild(DynamicFormComponent) filterForm: DynamicFormComponent;

    customBuilders: any[] = [];
    selectedCustomBuilder: any;

    storeId: any;
    cookieStore: any;

    sidebarVisible = false;
    createCustomBuilderModal = false;
    createCustomBuilderForm: FormGroup;
    errMsgs: any[] = [];

    routeRole: any;
    role: any;

    displayCategories: TreeNode[] = [];
    categories: any[] = [];
    selectedCategory = { label: 'No Category Selected', id: null, name: null, data: { id: null } };

    displayKioskCategories: TreeNode[] = [];
    kioskCategories: any[] = [];
    selectedKioskCategory = { label: 'No Kiosk Category Selected', id: null, data: { id: null } };

    constructor(
        public loader: LoadingBarService,
        public activatedRoute: ActivatedRoute,
        public router: Router,
        public globals: Globals,
        public fb: FormBuilder,
        public notificationService: NotificationService,
        public _customBuilderService: CustomBuilderService,
        public cookieService: CookieService,
        public _categoryService: CategoryService,
        public _kioskCategoryService: KioskCategoryService,
        public objectFilter: ObjectFilterPipe,
    ) {
        this.createCustomBuilderForm = fb.group({
            name: new FormControl('', [Validators.required]),
            description: new FormControl(''),
            basePrice: new FormControl(0, [Validators.required]),
            itemCategoryId: new FormControl(''),
            itemCategoryKioskId: new FormControl('')
        });

        this.role = globals.user.role;
        this.routeRole = globals.user.routeRole;

        // Handle Store Cookies
        const hasCurrentStore = cookieService.check('currentStore');
        if (hasCurrentStore) {
            this.cookieStore = JSON.parse(cookieService.get('currentStore'));
            this.storeId = this.cookieStore.id;
        } else {
            this.router.navigate(['/' + this.routeRole + '/select-store']);
        }
    }

    ngOnInit() {
        this.getAllBuildersByStore(this.storeId);
        this.getAllCategories();
        this.getAllKioskCategories();
    }

    public getAllBuildersByStore(id) {
        this._customBuilderService.getAllBuildersByStore(id)
            .subscribe((data: any) => {
                // console.log(data);
                this.customBuilders = data;
            });
    }

    // Handle onRowSelect (ie Select Single builder and navigate to inner view)
    onRowSelect(event) {
        if (this.role === 4) {
            this.router.navigate(['/super/custom-builders/' + event.data.id]);
        } else if (this.role === 3) {
            this.router.navigate(['/group/custom-builders/' + event.data.id]);
        } else if (this.role === 2) {
            this.router.navigate(['/owner/custom-builders/' + event.data.id]);
        } else {
            this.router.navigate(['/restricted']);
        }
    }

    public toggleCreateCustomBuilderModal() {
        this.createCustomBuilderModal = !this.createCustomBuilderModal;
    }

    public onCreateCustomBuilderFormSubmit() {
        const values = this.createCustomBuilderForm.value;
        // console.log(values);
        const obj = {
            basePrice: values.basePrice,
            description: values.description,
            itemCategoryId: values.itemCategoryId,
            itemCategoryKioskId: values.itemCategoryKioskId,
            name: values.name,
            storeId: this.storeId
        }
        this._customBuilderService.createBuilder(this.storeId, obj)
            .subscribe((data: any) => {
                // console.log(data);
                this.toggleCreateCustomBuilderModal();
                this.getAllBuildersByStore(this.storeId);
                this.notificationService.notify(
                    'success',
                    'Custom Builder Created',
                    'Custom Builder has successfully been created',
                    'top',
                    'right');
            });
    }

    // ************************************************************************* //
    // Functions to get, setup and handle Category Tree population and functions //
    // ************************************************************************* //

    public getAllCategories() {
        this.displayCategories = [];
        this._categoryService.getItemCategoriesByStoreId(this.storeId)
            .subscribe((data: any) => {
                this.categories = data;
                this.filterRootItemCategories(null);
            })
    }

    public getAllKioskCategories() {
        this.displayKioskCategories = [];
        this._kioskCategoryService.getItemKioskCategoriesByStoreId(this.storeId)
            .subscribe((data: any) => {
                this.kioskCategories = data;
                this.filterRootItemKioskCategories(null);
            })
    }

    public populateTreeMenu(array) {
        const filteredArray = array;
        filteredArray.forEach(element => {
            // console.log(element);
            const treeNodeObject = {
                label: element.name,
                data: element,
                id: element.id,
                parent: element.parentItemCategoryId,
                expandedIcon: 'fa fa-folder-open',
                collapsedIcon: 'fa fa-folder',
                leaf: false
            };
            this.displayCategories.push(treeNodeObject);
        });
    }

    public populateKioskTreeMenu(array) {
        const filteredArray = array;
        filteredArray.forEach(element => {
            // console.log(element);
            const treeNodeObject = {
                label: element.name,
                data: element,
                id: element.id,
                parent: element.parentItemCategoryId,
                expandedIcon: 'fa fa-folder-open',
                collapsedIcon: 'fa fa-folder',
                leaf: false
            };
            this.displayKioskCategories.push(treeNodeObject);
        });
    }

    public filterRootItemCategories(categoryId) {
        const catId = categoryId;
        const filteredCats = this.objectFilter.transform(this.categories, 'parentItemCategoryId', catId);
        this.populateTreeMenu(filteredCats);
    }

    public filterRootItemKioskCategories(kioskCategoryId) {
        const catId = kioskCategoryId;
        const filteredCats = this.objectFilter.transform(this.kioskCategories, 'parentItemCategoryId', catId);
        this.populateKioskTreeMenu(filteredCats);
    }

    public loadNode(event) {
        if (event.node) {
            const parentId = event.node.data.id;
            const children = this.objectFilter.transform(this.categories, 'parentItemCategoryId', parentId);
            children.forEach(element => {
                element.label = element.name;
                element.data = element;
                element.parent = element.parentItemCategoryId;
                element.expandedIcon = 'fa fa-folder-open';
                element.collapsedIcon = 'fa fa-folder';
                element.leaf = false;
            });
            event.node.children = children;
        }
    }

    public loadNodeKiosk(event) {
        if (event.node) {
            const parentId = event.node.data.id;
            const children = this.objectFilter.transform(this.kioskCategories, 'parentItemCategoryId', parentId);
            children.forEach(element => {
                element.label = element.name;
                element.data = element;
                element.parent = element.parentItemCategoryId;
                element.expandedIcon = 'fa fa-folder-open';
                element.collapsedIcon = 'fa fa-folder';
                element.leaf = false;
            });
            event.node.children = children;
        }
    }

    public nodeSelect(event) {
        this.selectedCategory.label = event.node.data.name;
        this.selectedCategory.id = event.node.data.id;
        this.selectedCategory.data = event.node.data;
        this.createCustomBuilderForm.patchValue({
            itemCategoryId: this.selectedCategory.id
        });
    }

    public nodeSelectKiosk(event) {
        this.selectedKioskCategory.label = event.node.data.name;
        this.selectedKioskCategory.id = event.node.data.id;
        this.selectedKioskCategory.data = event.node.data;
        this.createCustomBuilderForm.patchValue({
            itemCategoryKioskId: this.selectedKioskCategory.id
        });
    }

}



// WEBPACK FOOTER //
// ./src/app/custom-builders/custom-builders.component.ts